import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Calendar } from './ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format, addDays, isMonday } from 'date-fns';
import { CalendarIcon, Clock, Users, Check, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ⚠️ Set to `true` to re-enable online bookings. Set to `false` to show the
// "temporarily unavailable" notice while keeping all the underlying booking
// logic, admin dashboard, backend endpoints and email flow intact.
const RESERVATIONS_ONLINE_ENABLED = true;

export default function ReservationSection() {
  const { t } = useLanguage();

  // Temporary closed-for-online-bookings notice.
  // Toggle RESERVATIONS_ONLINE_ENABLED above to bring the form back.
  if (!RESERVATIONS_ONLINE_ENABLED) {
    return (
      <section
        id="reservations"
        data-testid="reservation-section"
        className="section-padding bg-[#FBF9F6]"
      >
        <div className="section-container">
          <div className="max-w-2xl mx-auto text-center">
            <p className="overline mb-4">{t('reservations.overline')}</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-[#2A1E17]">
              {t('reservations.title')}
            </h2>
            <div className="w-20 h-[2px] bg-[#8C5A2B] mt-6 mx-auto" />

            <div className="mt-10 bg-[#F0EAE1] border border-[#E6D8C8] p-8 md:p-12">
              <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-medium text-[#2A1E17] mb-4">
                {t('reservations.closed.title')}
              </h3>
              <p className="text-[#6B584B] text-base md:text-lg leading-relaxed mb-8">
                {t('reservations.closed.message')}
              </p>

              <a
                href="tel:+351932815611"
                data-testid="reservation-closed-phone-link"
                className="inline-flex items-center gap-3 bg-[#2A1E17] text-[#FBF9F6] px-8 py-4 uppercase tracking-[0.15em] text-sm font-medium hover:bg-[#8C5A2B] transition-colors"
              >
                <Phone size={18} strokeWidth={1.75} />
                +351 932 815 611
              </a>

              <div className="mt-8 pt-8 border-t border-[#E6D8C8] text-[#6B584B] text-sm space-y-2">
                <p className="flex items-center justify-center gap-2">
                  <Clock size={16} strokeWidth={1.5} className="text-[#8C5A2B]" />
                  {t('reservations.hours.monday')}
                </p>
                <p className="flex items-center justify-center gap-2">
                  <Clock size={16} strokeWidth={1.5} className="text-[#8C5A2B]" />
                  {t('reservations.hours.tuesday')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return <ReservationForm />;
}

function ReservationForm() {
  const { t } = useLanguage();
  const [date, setDate] = useState(null);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');
  const [reservationDetails, setReservationDetails] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    time: '',
    guests: '',
    special_requests: '',
  });

  // Fetch available slots when date changes
  useEffect(() => {
    if (date) {
      fetchAvailability(format(date, 'yyyy-MM-dd'));
    }
  }, [date]);

  const fetchAvailability = async (dateStr) => {
    setIsLoadingSlots(true);
    setError('');
    try {
      const response = await axios.get(`${API}/reservations/availability?date=${dateStr}`);
      setAvailableSlots(response.data.available_slots || []);
      if (response.data.message) {
        setError(response.data.message);
      }
    } catch (err) {
      console.error('Error fetching availability:', err);
      setError(t('reservations.errors.generic'));
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        ...formData,
        date: format(date, 'yyyy-MM-dd'),
        guests: parseInt(formData.guests),
      };

      const response = await axios.post(`${API}/reservations`, payload);
      setReservationDetails(response.data);
      setSubmitSuccess(true);
    } catch (err) {
      console.error('Error creating reservation:', err);
      const errorMsg = err.response?.data?.detail || t('reservations.errors.generic');
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = date && formData.name && formData.email && formData.phone && formData.time && formData.guests;

  // Disable Mondays and past dates
  const disabledDays = [
    { before: new Date() },
    { dayOfWeek: [1] }, // Monday
  ];

  if (submitSuccess && reservationDetails) {
    return (
      <section
        id="reservations"
        data-testid="reservation-section"
        className="section-padding bg-[#FBF9F6]"
      >
        <div className="section-container">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-16 h-16 bg-[#8C5A2B] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={32} className="text-[#FBF9F6]" />
            </div>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-[#2A1E17] mb-4">
              {t('reservations.success.title')}
            </h2>
            <p className="text-[#6B584B] mb-8">
              {t('reservations.success.message')}
            </p>
            
            <div className="bg-[#F0EAE1] p-8 border border-[#E6D8C8] text-left">
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A1E17] mb-6">
                {t('reservations.success.details')}
              </h3>
              <div className="space-y-4 text-[#6B584B]">
                <div className="flex justify-between border-b border-[#E6D8C8] pb-3">
                  <span>{t('reservations.form.name')}</span>
                  <span className="font-medium text-[#2A1E17]">{reservationDetails.name}</span>
                </div>
                <div className="flex justify-between border-b border-[#E6D8C8] pb-3">
                  <span>{t('reservations.form.date')}</span>
                  <span className="font-medium text-[#2A1E17]">{reservationDetails.date}</span>
                </div>
                <div className="flex justify-between border-b border-[#E6D8C8] pb-3">
                  <span>{t('reservations.form.time')}</span>
                  <span className="font-medium text-[#2A1E17]">{reservationDetails.time}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('reservations.form.guests')}</span>
                  <span className="font-medium text-[#2A1E17]">{reservationDetails.guests}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="reservations"
      data-testid="reservation-section"
      className="section-padding bg-[#FBF9F6]"
    >
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <div className="space-y-8">
            <div>
              <p className="overline mb-4">{t('reservations.overline')}</p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-[#2A1E17]">
                {t('reservations.title')}
              </h2>
              <div className="w-20 h-[2px] bg-[#8C5A2B] mt-6" />
            </div>
            
            <p className="text-[#6B584B] text-lg leading-relaxed">
              {t('reservations.description')}
            </p>

            {/* Opening Hours */}
            <div className="bg-[#F0EAE1] p-8 border border-[#E6D8C8]">
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A1E17] mb-6">
                {t('reservations.hours.title')}
              </h3>
              <div className="space-y-3 text-[#6B584B]">
                <p className="flex items-center gap-2">
                  <Clock size={18} strokeWidth={1.5} className="text-[#8C5A2B]" />
                  {t('reservations.hours.monday')}
                </p>
                <p className="flex items-center gap-2">
                  <Clock size={18} strokeWidth={1.5} className="text-[#8C5A2B]" />
                  {t('reservations.hours.tuesday')}
                </p>
              </div>
              <div className="mt-6 pt-6 border-t border-[#E6D8C8]">
                <a
                  href="tel:+351932815611"
                  data-testid="reservation-phone-link"
                  className="inline-flex items-center gap-2 text-[#8C5A2B] hover:text-[#A8723E] transition-colors"
                >
                  <Phone size={18} strokeWidth={1.5} />
                  <span className="font-medium">+351 932 815 611</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-[#F0EAE1] p-8 md:p-10 border border-[#E6D8C8]">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#2A1E17] text-sm font-medium">
                  {t('reservations.form.name')}
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  data-testid="reservation-name-input"
                  className="bg-[#FBF9F6] border-[#E6D8C8] rounded-none focus:ring-1 focus:ring-[#8C5A2B] focus:border-[#8C5A2B]"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#2A1E17] text-sm font-medium">
                  {t('reservations.form.email')}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  data-testid="reservation-email-input"
                  className="bg-[#FBF9F6] border-[#E6D8C8] rounded-none focus:ring-1 focus:ring-[#8C5A2B] focus:border-[#8C5A2B]"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#2A1E17] text-sm font-medium">
                  {t('reservations.form.phone')}
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  data-testid="reservation-phone-input"
                  className="bg-[#FBF9F6] border-[#E6D8C8] rounded-none focus:ring-1 focus:ring-[#8C5A2B] focus:border-[#8C5A2B]"
                />
              </div>

              {/* Date Picker */}
              <div className="space-y-2">
                <Label className="text-[#2A1E17] text-sm font-medium">
                  {t('reservations.form.date')}
                </Label>
                <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="reservation-date-trigger"
                      className="w-full flex items-center justify-between px-4 py-3 bg-[#FBF9F6] border border-[#E6D8C8] text-left text-[#2A1E17] hover:bg-[#FBF9F6]/80 transition-colors"
                    >
                      <span className={date ? 'text-[#2A1E17]' : 'text-[#6B584B]'}>
                        {date ? format(date, 'PPP') : t('reservations.form.date')}
                      </span>
                      <CalendarIcon size={18} className="text-[#8C5A2B]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#FBF9F6] border-[#E6D8C8]" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => {
                        setDate(d);
                        if (d) setDatePopoverOpen(false);
                      }}
                      disabled={disabledDays}
                      initialFocus
                      data-testid="reservation-calendar"
                      className="rounded-none"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time Select */}
              <div className="space-y-2">
                <Label className="text-[#2A1E17] text-sm font-medium">
                  {t('reservations.form.time')}
                </Label>
                <Select
                  value={formData.time}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, time: value }))}
                  disabled={!date || isLoadingSlots || availableSlots.length === 0}
                >
                  <SelectTrigger
                    data-testid="reservation-time-trigger"
                    className="bg-[#FBF9F6] border-[#E6D8C8] rounded-none focus:ring-1 focus:ring-[#8C5A2B]"
                  >
                    <SelectValue placeholder={t('reservations.form.selectTime')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FBF9F6] border-[#E6D8C8]">
                    {availableSlots.map((slot) => (
                      <SelectItem
                        key={slot.time}
                        value={slot.time}
                        data-testid={`time-slot-${slot.time}`}
                      >
                        {slot.time} ({slot.available_tables} {slot.available_tables === 1 ? 'table' : 'tables'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Guests Select */}
              <div className="space-y-2">
                <Label className="text-[#2A1E17] text-sm font-medium">
                  {t('reservations.form.guests')}
                </Label>
                <Select
                  value={formData.guests}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, guests: value }))}
                >
                  <SelectTrigger
                    data-testid="reservation-guests-trigger"
                    className="bg-[#FBF9F6] border-[#E6D8C8] rounded-none focus:ring-1 focus:ring-[#8C5A2B]"
                  >
                    <SelectValue placeholder={t('reservations.form.selectGuests')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FBF9F6] border-[#E6D8C8]">
                    {[...Array(10)].map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)} data-testid={`guests-${i + 1}`}>
                        {i + 1} {i === 0 ? t('reservations.form.guestLabel') : t('reservations.form.guestsLabel')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Special Requests */}
              <div className="space-y-2">
                <Label htmlFor="special_requests" className="text-[#2A1E17] text-sm font-medium">
                  {t('reservations.form.specialRequests')}
                </Label>
                <Textarea
                  id="special_requests"
                  name="special_requests"
                  value={formData.special_requests}
                  onChange={handleInputChange}
                  placeholder={t('reservations.form.specialRequestsPlaceholder')}
                  data-testid="reservation-special-requests"
                  className="bg-[#FBF9F6] border-[#E6D8C8] rounded-none focus:ring-1 focus:ring-[#8C5A2B] focus:border-[#8C5A2B] min-h-[100px]"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                data-testid="reservation-submit-btn"
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('reservations.form.submitting') : t('reservations.form.submit')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
